import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import type { User as SupabaseUser } from '@supabase/supabase-js';

interface User {
  username: string;
  role: 'admin';
  id: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser({
          username: session.user.email?.split('@')[0] || 'admin',
          role: 'admin',
          id: session.user.id
        });
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser({
          username: session.user.email?.split('@')[0] || 'admin',
          role: 'admin',
          id: session.user.id
        });
      } else {
        setUser(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = async (username: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: username,
        password: password
      });

      if (error) {
        console.error('Login error:', error);
        return { success: false, error: 'Invalid email or password' };
      }

      if (data.user) {
        setUser({
          username: data.user.email?.split('@')[0] || 'admin',
          role: 'admin',
          id: data.user.id
        });
        return { success: true };
      }

      return { success: false, error: 'Invalid email or password' };
    } catch (err) {
      console.error('Login exception:', err);
      if (err instanceof TypeError && err.message.includes('fetch')) {
        return { success: false, error: 'Supabase connection failed' };
      }
      return { success: false, error: 'Supabase connection failed' };
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      login,
      logout,
      loading
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};